import { useId, useState } from 'react';
import Icon from './Icon';

// A labelled form field with a clickable ⓘ that expands an explanation INLINE,
// below the control.
//
// Not a tooltip: tooltips need a hover, which phones do not have, and they
// vanish the moment you move toward the thing you were about to change. These
// notes explain settings whose effect is invisible until after a save — exactly
// the case where the text has to stay put while the reader looks back at the
// field.
//
// The note renders AFTER the control, not between the label and it. Expanding
// it in the middle pushes the select out from under the cursor that was on its
// way there.
//
// The trigger is a sibling of the <label>, never a child: a <button> inside a
// <label> forwards its click to the labelled control, so opening the note would
// also drop focus into the select and, on some browsers, open it.
//
// Collapsed by default — an always-open explanation stops being read after the
// second visit and costs vertical space on every visit after that.
export default function InfoNote({
  id,              // the control's id; also ties the <label> to it
  label,           // the field's visible name
  triggerLabel,    // accessible name for the ⓘ, e.g. "מה זו יחידת היעד"
  note,            // the explanation
  className,
  style,
  children,        // the control itself
}) {
  const [open, setOpen] = useState(false);
  const noteId = useId();

  return (
    <div className={className} style={style}>
      <span className="sheet-settings-label">
        <label htmlFor={id}>{label}</label>
        <button
          type="button"
          className="info-note-trigger"
          aria-expanded={open}
          aria-controls={noteId}
          aria-label={triggerLabel}
          onClick={() => setOpen(o => !o)}
        >
          <Icon name="info" size={13} />
        </button>
      </span>

      {children}

      {open && (
        <div id={noteId} className="info-note-body" role="note">
          {note}
        </div>
      )}
    </div>
  );
}
